import React, { useState } from 'react';
import { Input } from '../../../../components/input';
import { Select } from '../../../../components/select';
import editIcon from '../../../../../assets/icons/edit.png';
import addIcon from '../../../../../assets/icons/add.png';
import facebookIcon from '../../../../../assets/images/facebook-blue.png';
import instagramIcon from '../../../../../assets/images/instagram-blue.png';
import linkedinIcon from '../../../../../assets/images/linkedin-blue.png';
import { SocialMediaItem, CompanyProfile } from '../types';
import { SOCIAL_MEDIA_OPTIONS } from '../constants';

interface SocialMediaFieldProps {
  icon: any;
  label: string;
  companyProfile: CompanyProfile;
  isEditing: boolean;
  editingSocialMedia: SocialMediaItem[];
  loading: boolean;
  showingSocialMedia: boolean;
  onStartEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
  onToggleDisplay: () => void;
  onAddSocialMedia: () => void;
  onUpdateType: (id: string, type: string) => void;
  onUpdateUrl: (id: string, url: string) => void;
  onRemoveSocialMedia: (id: string) => void;
}

const renderGoogleIcon = (): JSX.Element => {
  return (
    <svg 
      className='social-media-icon google-icon'
      width="18" 
      height="18" 
      viewBox="0 0 24 24" 
      fill="none" 
      xmlns="http://www.w3.org/2000/svg"
    >
      <path 
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" 
        fill="#4285F4"
      />
      <path 
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" 
        fill="#34A853"
      />
      <path 
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" 
        fill="#FBBC05"
      />
      <path 
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" 
        fill="#EA4335"
      />
    </svg>
  );
};

const getSocialMediaIcon = (type: string): any => {
  const iconMap: { [key: string]: any } = {
    'facebook': facebookIcon,
    'instagram': instagramIcon,
    'linkedin': linkedinIcon,
    'google': 'google'
  };
  return iconMap[type] || null;
};

const getSocialMediaLabel = (type: string): string => {
  const option = SOCIAL_MEDIA_OPTIONS.find(opt => opt.value === type);
  return option ? option.label : type;
};

export const SocialMediaField: React.FC<SocialMediaFieldProps> = ({
  icon,
  label,
  companyProfile,
  isEditing,
  editingSocialMedia,
  loading,
  showingSocialMedia,
  onStartEdit,
  onSave,
  onCancel,
  onToggleDisplay,
  onAddSocialMedia,
  onUpdateType,
  onUpdateUrl,
  onRemoveSocialMedia
}) => {
  const socialMedia = companyProfile?.social_media || [];
  const hasSocialMedia = socialMedia && socialMedia.length > 0;

  const getAvailableSocialMediaOptions = (excludeId?: string): Array<{ value: string; label: string }> => {
    const usedTypes = editingSocialMedia
      .filter((item: SocialMediaItem) => item.id !== excludeId)
      .map((item: SocialMediaItem) => item.type);
    
    return SOCIAL_MEDIA_OPTIONS.filter(option => !usedTypes.includes(option.value));
  };

  const hasAvailableSocialMediaTypes = (): boolean => {
    const usedTypes = editingSocialMedia.map((item: SocialMediaItem) => item.type);
    return usedTypes.length < SOCIAL_MEDIA_OPTIONS.length;
  };

  if (isEditing) {
    return (
      <div className='field-row editing'>
        {icon && <img src={icon} alt={label} className='field-icon' />}
        <div className='edit-controls social-media-edit'>
          {editingSocialMedia.map((social: SocialMediaItem) => {
            const socialId = social.id || `social-${Date.now()}`;
            const availableOptions = getAvailableSocialMediaOptions(socialId);
            const options = availableOptions.some(opt => opt.value === social.type)
              ? availableOptions
              : [...availableOptions, SOCIAL_MEDIA_OPTIONS.find(opt => opt.value === social.type)!];
            
            return (
              <div key={socialId} className='social-media-item' style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: '20px' }}>
                <div className='social-media-field-group'>
                  <Select
                    name={`social_media_type_${socialId}`}
                    options={options}
                    value={social.type}
                    allowEmpty={false}
                    onChange={(val) => onUpdateType(socialId, val)}
                  />
                </div>
                <div className='social-media-field-group'>
                  <Input
                    name={`social_media_url_${socialId}`}
                    type='text'
                    value={social.url}
                    onChange={(val) => onUpdateUrl(socialId, val)}
                    placeholder='Ex: instagram.com/mecanica_500'
                  />
                </div>
                <button
                  className='remove-btn'
                  onClick={() => onRemoveSocialMedia(socialId)}
                  type='button'
                  aria-label={`Remover rede social ${social.type}`}
                >
                  Remover
                </button>
              </div>
            );
          })}
          {hasAvailableSocialMediaTypes() && (
            <button
              className='add-btn'
              onClick={onAddSocialMedia}
              type='button'
              disabled={loading}
              aria-label='Adicionar nova rede social'
            >
              + Adicionar rede social
            </button>
          )}
          <div className='edit-actions'>
            <button
              className='save-btn'
              onClick={onSave}
              disabled={loading}
              aria-label='Salvar alterações das redes sociais'
            >
              Salvar
            </button>
            <button
              className='cancel-btn'
              onClick={onCancel}
              type='button'
              aria-label='Cancelar edição das redes sociais'
            >
              Cancelar
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='field-row'>
      {icon && <img src={icon} alt={label} className='field-icon' />}
      <div className='field-content'>
        <span 
          className={`field-value clickable ${!hasSocialMedia ? 'no-social-media-tooltip' : ''}`}
          onClick={onToggleDisplay}
          role='button'
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onToggleDisplay();
            }
          }}
          aria-label='Ver redes sociais'
        >
          Ver redes sociais
        </span>
        {showingSocialMedia && hasSocialMedia && (
          <div className='social-media-list-display'>
            {socialMedia.map((social: SocialMediaItem) => {
              const socialIcon = getSocialMediaIcon(social.type);
              const socialLabel = getSocialMediaLabel(social.type);
              return (
                <div key={social.id || social.type} className='social-media-display-item'>
                  {socialIcon === 'google' ? (
                    renderGoogleIcon()
                  ) : (
                    <img src={socialIcon} alt={socialLabel} className='social-media-icon' />
                  )}
                  <span className='social-media-name'>{socialLabel}:</span>
                  <span className='social-media-url'>{social.url}</span>
                </div>
              );
            })}
          </div>
        )}
        {showingSocialMedia && !hasSocialMedia && (
          <div className='social-media-empty-message'>
            Nenhuma rede social cadastrada
          </div>
        )}
      </div>
      <img 
        src={hasSocialMedia ? editIcon : addIcon} 
        alt={hasSocialMedia ? 'edit' : 'adicionar'} 
        className='edit-icon' 
        onClick={onStartEdit}
      />
    </div>
  );
};

